"use client";

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { Linkedin, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export function AboutFounders() {
  const [openFounders, setOpenFounders] = useState<{[key: string]: boolean}>({
    lars: false,
    thomas: false
  });

  const toggleFounder = (name: string) => {
    setOpenFounders(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-h2 mb-2">Unsere Gründer</h2>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full" />
        </motion.div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Dr. Lars Hewel Card */}
          <div className="relative h-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={cn(
                "rounded-xl shadow-neumorphic overflow-hidden transition-all duration-500 h-full",
                openFounders.lars 
                  ? 'shadow-lg'
                  : 'max-h-[140px] hover:shadow-md cursor-pointer'
              )}
              style={{ 
                transformOrigin: 'top center',
                zIndex: openFounders.lars ? 10 : 1
              }}
            >
              <div className="bg-gradient-floral p-6 relative cursor-pointer" onClick={() => toggleFounder('lars')}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-card flex-shrink-0 overflow-hidden flex items-center justify-center relative border-2 border-primary">
                      <User className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-h3 text-foreground">Dr. Lars Hewel</h3>
                      <p className="text-muted-foreground font-medium">CEO & Co-Founder</p>
                    </div>
                  </div>
                  <Link 
                    href="https://www.linkedin.com/in/lars-hewel/" 
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Lars Hewel's LinkedIn Profile"
                  >
                    <Button variant="secondary" size="sm" className="gap-2">
                      <Linkedin className="h-4 w-4" />
                      <span className="hidden sm:inline">LinkedIn</span>
                    </Button>
                  </Link>
                </div>
                
                {!openFounders.lars && (
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center pointer-events-none">
                    <div className="h-6 w-12 bg-primary/20 rounded-t-xl flex items-center justify-center text-primary text-xs">
                      Mehr
                    </div>
                  </div>
                )}
              </div>
              
              <div className={cn(
                "bg-card p-6 pb-10 flex flex-col h-full transition-opacity duration-300",
                openFounders.lars ? "opacity-100" : "opacity-0"
              )}>
                <div className="mb-6 space-y-4 flex-grow">
                  <p className="text-body-md">
                    Lars hat über 25 Jahre Erfahrung in der Führung und Beratung von Unternehmen. Seit Beginn seiner Karriere arbeitet er an der Transformation von Unternehmen unterschiedlicher Größe und Eigentümerstrukturen im Hinblick auf Geschäftsmodell, operative Exzellenz und Digitalisierung. Schon immer hat er in seinen Tätigkeiten als &quot;Intrapreneur&quot; gehandelt - etwa beim Aufbau von Joint Ventures, neuen Geschäftsmodellen und -bereichen.
                  </p>
                  
                  <p className="text-body-md">
                    Ein Schwerpunkt seiner Tätigkeit lag in der HR-Services-Industrie. Hier entwickelte er nachhaltig das Operating Model des Marktführers für Personaldienstleistungen weiter. Zuletzt wirkte er als CEO des niederländischen Dienstleisters YoungCapital, dessen deutsche Gesellschaften er nach erfolgreicher Restrukturierung an den Marktführer für studentische Personaldienstleistungen, Jobvalley, verkaufte.
                  </p>
                  
                  <p className="text-body-md">
                    Seine Entscheidung, gemeinsam mit Thomas Krier Umynd zu gründen, ist der Überzeugung geschuldet, dass Rekrutierungsprozesse so lange dysfunktional bleiben werden, wie sie nicht durch Technologie personalisiert und dadurch &quot;menschlich&quot; werden.
                  </p>
                  
                  <p className="text-body-md">
                    Lars verbindet eine jahrzehntelange Freundschaft mit seinem &quot;AI-Sensei&quot; Thomas - der ihn schon früh mit auf die Reise in Richtung AI genommen hat.
                  </p>
                </div>
                
                <div className="flex justify-between mt-auto">
                  <Link href="https://www.linkedin.com/in/lars-hewel/" target="_blank">
                    <Button variant="secondary">LinkedIn Profil</Button>
                  </Link>
                  {openFounders.lars && (
                    <div 
                      className="absolute bottom-0 left-0 right-0 flex justify-center cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleFounder('lars'); }}
                    >
                      <div className="h-6 w-16 bg-muted/50 rounded-t-xl flex items-center justify-center text-muted-foreground text-xs hover:bg-muted/70 transition-colors">
                        Weniger
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Thomas Krier Card */}
          <div className="relative h-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={cn(
                "rounded-xl shadow-neumorphic overflow-hidden transition-all duration-500 h-full",
                openFounders.thomas 
                  ? 'shadow-lg'
                  : 'max-h-[140px] hover:shadow-md cursor-pointer'
              )}
              style={{ 
                transformOrigin: 'top center',
                zIndex: openFounders.thomas ? 10 : 1
              }}
            >
              <div className="bg-gradient-floral p-6 relative cursor-pointer" onClick={() => toggleFounder('thomas')}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-card flex-shrink-0 overflow-hidden flex items-center justify-center relative border-2 border-primary">
                      <User className="w-10 h-10 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-h3 text-foreground">Thomas Krier</h3>
                      <p className="text-muted-foreground font-medium">CIO & Co-Founder</p>
                    </div>
                  </div>
                  <Link 
                    href="https://www.linkedin.com/in/krier/" 
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Thomas Krier's LinkedIn Profile"
                  >
                    <Button variant="secondary" size="sm" className="gap-2">
                      <Linkedin className="h-4 w-4" />
                      <span className="hidden sm:inline">LinkedIn</span>
                    </Button>
                  </Link>
                </div>
                
                {!openFounders.thomas && (
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center pointer-events-none">
                    <div className="h-6 w-12 bg-primary/20 rounded-t-xl flex items-center justify-center text-primary text-xs">
                      Mehr
                    </div>
                  </div>
                )}
              </div>
              
              <div className={cn(
                "bg-card p-6 pb-10 flex flex-col h-full transition-opacity duration-300",
                openFounders.thomas ? "opacity-100" : "opacity-0"
              )}>
                <div className="mb-6 space-y-4 flex-grow">
                  <p className="text-body-md">
                    Thomas ist erfahrener Datenexperte, KI-Visionär und Builder innovativer Technologie-Lösungen mit über 20 Jahren Praxis in Konzeption, Aufbau und Skalierung hochentwickelter Data-Intelligence-Systeme. Seit seinem Studium der Wirtschaftsinformatik befasst er sich intensiv mit Künstlicher Intelligenz.
                  </p>
                  
                  <p className="text-body-md">
                    Als Pionier im Bereich der Generativen KI entwickelt er mit einem spezialisierten Team hochautomatisierte Agenten-Systeme für strategische Marktanalysen und datenbasierte Entscheidungsunterstützung. Kern seiner Tätigkeit ist die Verknüpfung von Daten, KI und Business Intelligence zu praxistauglichen Lösungen, wie z.B. einer Predictive Engine zur Preis- und Conversion-Optimierung in der Versicherungswirtschaft.
                  </p>
                  
                  <p className="text-body-md">
                    Bei Umynd bringt Thomas seine Erfahrung im Aufbau komplexer Multiagentensysteme mit neuesten multimodalen Modellen zusammen, um Recruiting-Prozesse effizienter und menschlicher zu gestalten. Sein Grundsatz: Menschen, die KI intelligent nutzen, prägen die Zukunft erfolgreicher Arbeitswelten.
                  </p>
                  
                  <p className="text-body-md">
                    Mit Lars verbindet ihn eine jahrzehntelange Freundschaft und ein intensiver Dialog über die transformative Kraft von Technologie. Die Gründung von Umynd resultiert aus dieser Überzeugung und der Einsicht, dass AI-getriebene Innovationen in neuen Strukturen freier entstehen.
                  </p>
                </div>
                
                <div className="flex justify-between mt-auto">
                  <Link href="https://www.linkedin.com/in/krier/" target="_blank">
                    <Button variant="secondary">LinkedIn Profil</Button>
                  </Link>
                  {openFounders.thomas && (
                    <div 
                      className="absolute bottom-0 left-0 right-0 flex justify-center cursor-pointer" 
                      onClick={(e) => { e.stopPropagation(); toggleFounder('thomas'); }}
                    >
                      <div className="h-6 w-16 bg-muted/50 rounded-t-xl flex items-center justify-center text-muted-foreground text-xs hover:bg-muted/70 transition-colors">
                        Weniger
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
} 